import { Link } from 'react-router-dom';
import { FileText, ChevronRight, Building2, CalendarClock, Timer } from 'lucide-react';
import { useTranslation } from '../../i18n';

interface SettingsCard {
  to: string;
  icon: typeof FileText;
  titleKey: string;
  descKey: string;
}

const cards: SettingsCard[] = [
  {
    to: '/settings/tenant',
    icon: Building2,
    titleKey: 'tenantSettings:card.title',
    descKey: 'tenantSettings:card.description',
  },
  {
    to: '/settings/absences',
    icon: CalendarClock,
    titleKey: 'absenceSettings:title',
    descKey: 'absenceSettings:subtitle',
  },
  {
    to: '/settings/timeclock-groups',
    icon: Timer,
    titleKey: 'timeClockGroups:card.title',
    descKey: 'timeClockGroups:card.description',
  },
  {
    to: '/settings/text-snippets',
    icon: FileText,
    titleKey: 'textSnippets:settings.cards.textSnippets.title',
    descKey: 'textSnippets:settings.cards.textSnippets.description',
  },
];

export default function SettingsPage() {
  const { t } = useTranslation(['textSnippets', 'tenantSettings', 'absenceSettings', 'timeClockGroups']);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header card */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900">
          {t('textSnippets:settings.title')}
        </h1>
        <p className="text-[13px] text-slate-500 mt-0.5">
          {t('textSnippets:settings.subtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {cards.map((card) => (
          <Link
            key={card.to}
            to={card.to}
            className="group rounded-xl border border-slate-200 bg-white p-4 hover:border-[#00A871]/40 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00A871]/40 transition-all"
          >
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-md bg-emerald-50 text-[#00A871] flex items-center justify-center flex-shrink-0">
                <card.icon className="w-4 h-4" strokeWidth={2} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-[15px] font-semibold text-slate-900 tracking-tight">
                    {t(card.titleKey)}
                  </h3>
                  <ChevronRight
                    className="w-4 h-4 text-slate-400 group-hover:text-[#00A871] transition-colors flex-shrink-0"
                    strokeWidth={2}
                  />
                </div>
                <p className="text-[13px] text-slate-500 mt-1">
                  {t(card.descKey)}
                </p>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
